// src/pages/CookiePolicyPage.js
import React from 'react';
import MainNavbar from '../components/MainNavbar';
import MainFooter from '../components/MainFooter';

const CookiePolicyPage = () => {
  return (
    <div className="flex flex-col min-h-screen bg-background-primary">
      <MainNavbar />

      <main className="flex-grow">
        <div className="max-w-4xl mx-auto py-16 px-4 sm:px-6 lg:px-8">
          {/* Main Page Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-extrabold text-content-primary sm:text-5xl tracking-tight">Cookie Policy</h1>
            <p className="mt-4 text-lg text-content-secondary">Last updated: March 6, 2026</p>
          </div>

          {/* Article content with prose styling */}
          <article className="prose prose-lg lg:prose-xl max-w-none dark:prose-invert">

            <h2>1. Introduction</h2>
            <p>
              This Cookie Policy explains how Kikikata and our third-party partners use cookies, browser local storage, and similar technologies
              when you visit our website. It also covers what choices you have regarding these technologies.
            </p>

            <h2>2. What Are Cookies?</h2>
            <p>
              Cookies are small data files stored on your device by your browser when you visit a website. They help websites recognize returning
              users, remember preferences, and collect usage data. Similar technologies include browser local storage and IndexedDB, which serve
              comparable purposes but are stored differently on your device.
            </p>

            <h2>3. Cookies Set by Third-Party Services</h2>
            <p>
              Kikikata uses third-party services that may place cookies on your device. These are outside our direct control, and each provider
              is responsible for its own cookie practices.
            </p>

            <h3>3.1 Firebase (Google)</h3>
            <p>
              Firebase Authentication uses browser local storage and IndexedDB (not traditional cookies) to persist your login session across
              page reloads. Firebase may also use cookies for analytics and performance monitoring. These are essential for the service to function.
            </p>
            <p>
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google Privacy Policy</a>
            </p>

            <h3>3.2 PayPal</h3>
            <p>
              The PayPal SDK is loaded on our site to enable payment processing. PayPal may set cookies for fraud prevention, security, and to
              remember your PayPal session. These cookies are set by PayPal&apos;s domains and governed by their policy.
            </p>
            <p>
              <a href="https://www.paypal.com/us/legalhub/privacy-full" target="_blank" rel="noopener noreferrer">PayPal Privacy Policy</a>
            </p>

            <h3>3.3 Crisp (Live Chat)</h3>
            <p>
              Crisp is our live support chat widget, loaded for logged-in users. Crisp sets cookies and uses local storage to identify returning
              visitors, store your chat session, and provide support functionality. Crisp may set cookies from <code>crisp.chat</code> domains.
            </p>
            <p>
              <a href="https://crisp.chat/en/privacy/" target="_blank" rel="noopener noreferrer">Crisp Privacy Policy</a>
            </p>

            <h2>4. Browser Local Storage We Use Directly</h2>
            <p>
              Kikikata itself stores the following data in your browser&apos;s local storage. This data stays on your device and is never transmitted
              to our servers. It is used purely to remember your preferences between visits.
            </p>
            <ul>
              <li>
                <strong>Session settings</strong> (<code>sessionSettings</code>): Your study session preferences, such as whether sound effects,
                animations, furigana, translations, and karaoke mode are enabled, and which input mode you use.
              </li>
              <li>
                <strong>Theme preference</strong> (<code>theme</code>): Whether you have chosen light or dark mode.
              </li>
              <li>
                <strong>Audio playback speed</strong> (<code>universalAudioSpeed</code>): Your preferred speed for audio playback.
              </li>
              <li>
                <strong>Furigana visibility</strong> (<code>furiganaVisibility</code>): Whether furigana is shown above kanji during sessions.
              </li>
              <li>
                <strong>Translation visibility</strong> (<code>translationVisibility</code>): Whether English translations are displayed
                during sessions.
              </li>
            </ul>
            <p>
              You can clear local storage at any time through your browser&apos;s settings. Doing so will reset your session preferences to their
              defaults on your next visit.
            </p>

            <h2>5. Types of Cookies by Purpose</h2>
            <ul>
              <li>
                <strong>Essential / Authentication</strong>: Required for you to log in and stay logged in. Set by Firebase Authentication.
                The site cannot function correctly without these.
              </li>
              <li>
                <strong>Payment &amp; Security</strong>: Set by PayPal to enable secure payment processing and prevent fraud.
              </li>
              <li>
                <strong>Support</strong>: Set by Crisp to provide and remember your live chat sessions.
              </li>
              <li>
                <strong>Analytics &amp; Performance</strong>: May be set by Firebase to help us understand how users interact with the site.
                This data is aggregated and anonymous.
              </li>
            </ul>

            <h2>6. How to Manage Cookies</h2>
            <p>
              Most web browsers allow you to view, block, or delete cookies through their settings. You can also clear local storage via your
              browser&apos;s developer tools or privacy settings. Please be aware that blocking essential cookies will prevent you from logging in
              and using account-based features.
            </p>
            <p>
              For guidance on managing cookies in your browser, visit{' '}
              <a href="https://www.aboutcookies.org" target="_blank" rel="noopener noreferrer">AboutCookies.org</a>.
            </p>
            <p>
              To opt out of Google Analytics, you can use the{' '}
              <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">Google Analytics Opt-out Browser Add-on</a>.
            </p>

            <h2>7. Updates to This Cookie Policy</h2>
            <p>
              We may update this Cookie Policy as our services evolve or in response to changes in regulations. Any updates will be reflected
              by the date at the top of this page. We encourage you to review this policy periodically.
            </p>

            <h2>8. Contact Us</h2>
            <p>
              If you have questions or concerns about our use of cookies or local storage, please contact us at{' '}
              <a href="mailto:contact@kikikata.app">contact@kikikata.app</a>.
            </p>
          </article>
        </div>
      </main>

      <MainFooter />
    </div>
  );
};

export default CookiePolicyPage;